import React, { useEffect, useMemo } from 'react';
import Header from '@src/pages/workflow/components/Validation/Flow/Header';
import {
  StyledContent,
  StyledWrapper,
} from '@src/pages/workflow/components/Validation/Flow/styled';
import SummaryCard from '@src/pages/workflow/components/Validation/Flow/SummaryCard';
import CoverageDetail from '@src/pages/workflow/components/Validation/Flow/CoverageDetail';
import { useCoverageReport } from '@src/pages/workflow/api/queryHook';
import { Form, message, Spin } from 'antd';
import { arrayGroupBy } from '@src/pages/workflow/utils';
import moment from 'moment';
import { useFlowStore } from '@src/pages/workflow/store/flowBuilder';
import { trimMappedEventRules } from '@components/mapping-panel/utils/trimUtils';
import { useParams } from 'react-router-dom';
import { useGetDimensionsList } from '@api/common/queryHooks';
import { useMetricBuilderStore } from '@src/emp-3.0/components/Metrics/IndividualMetric/store';
import { EMetricTypes, ESystemType } from '@src/emp-3.0/types';
import { useGetEventByIdOrName } from '@src/emp-3.0/hooks';
import { useIsFetching } from '@tanstack/react-query';
import { GET_EVENT_LIST_FOR_METRIC } from '@src/emp-3.0/utils';
import { calcShortcutIntervalWithinBoundary } from '@src/utils/calcShortcutInterval';
import { DATE_SHORTCUTS } from '@src/utils/constants';
import { EventType, getEventParam } from './utils';

const Validation: React.FC = () => {
  const [tableData, setTableData] = React.useState([]);
  const [summaryData, setSummaryData] = React.useState({});
  const [dimensions, setDimensions] = React.useState<any>([]);
  const [activeDimension, setActiveDimension] = React.useState<any>(null);
  const { mutate: query, isLoading } = useCoverageReport();
  const { data: dimensionListRes, isFetching: isDimensionFetching } = useGetDimensionsList();
  const isEventFetching = useIsFetching([GET_EVENT_LIST_FOR_METRIC]) > 0;

  const { formInstance: rawFlowForm, setActiveTool } = useFlowStore();
  const { formInstance: rawMetricForm } = useMetricBuilderStore();

  // to handle cases where formInstance is not set yet
  const [dummyFlowForm] = Form.useForm();
  const [dummyMetricForm] = Form.useForm();
  const flowForm = rawFlowForm ?? dummyFlowForm;
  const metricForm = rawMetricForm ?? dummyMetricForm;
  const metricType = Form.useWatch('metrics_type', metricForm);

  const flowInitialEventId = Form.useWatch(['definition', 'events', 'start_event'], flowForm);
  const flowCompleteEventId = Form.useWatch(['definition', 'events', 'success_event'], flowForm);
  const metricInitialEventId = Form.useWatch(['definition', 'first_event'], metricForm);
  const metricCompleteEventId = Form.useWatch(['definition', 'second_event'], metricForm);
  const metricSingleEventId = Form.useWatch(['definition', 'event'], metricForm);
  const numeratorEventId = Form.useWatch(['definition', 'numerator', 'event'], metricForm);
  const denominatorEventId = Form.useWatch(['definition', 'denominator', 'event'], metricForm);

  const flowInitialEvent = useGetEventByIdOrName(flowInitialEventId);
  const flowCompleteEvent = useGetEventByIdOrName(flowCompleteEventId);
  const metricInitialEvent = useGetEventByIdOrName(metricInitialEventId);
  const metricCompleteEvent = useGetEventByIdOrName(metricCompleteEventId);
  const metricSingleEvent = useGetEventByIdOrName(metricSingleEventId);
  const metricNumeratorEvent = useGetEventByIdOrName(numeratorEventId);
  const metricDenominatorEvent = useGetEventByIdOrName(denominatorEventId);
  const { type } = useParams();

  const last24Hours = calcShortcutIntervalWithinBoundary(
    DATE_SHORTCUTS.past24Hours,
    ESystemType.EMP,
  );
  const queryTimeRange = `${last24Hours[0].format('YYYY-MM-DD HH:mm:ssZ')}/${last24Hours[1].format(
    'YYYY-MM-DD HH:mm:ssZ',
  )}`;
  const dimensionKey = useMemo(() => activeDimension?.dimension_name, [activeDimension]);

  const { eventParams, eventTypes } = useMemo(() => {
    const events: any = [];
    const eventTypes: EventType[] = [];
    if (!dimensionKey) {
      return { events, eventTypes };
    }
    function push(eventType: EventType, eventInfo) {
      if (!eventInfo) {
        return;
      }
      eventTypes.push(eventType);
      events.push(getEventParam(eventType, eventInfo));
    }
    if (type === 'flow') {
      push('flow-initial', flowInitialEvent);
      push('flow-complete', flowCompleteEvent);
    }
    if (type === 'metric') {
      if (metricType === EMetricTypes.SINGLE_EVENT) {
        push('metric-single', metricSingleEvent);
      } else if (metricType === EMetricTypes.RATIO_METRIC) {
        push('metric-ratio-numerator', metricNumeratorEvent);
        push('metric-ratio-denominator', metricDenominatorEvent);
      } else {
        push('metric-initial', metricInitialEvent);
        push('metric-complete', metricCompleteEvent);
      }
    }
    return { eventParams: events, eventTypes };
  }, [
    activeDimension,
    flowInitialEvent,
    flowCompleteEvent,
    metricInitialEvent,
    metricCompleteEvent,
    metricSingleEventId,
    flowInitialEventId,
    flowCompleteEventId,
    metricInitialEventId,
    metricCompleteEventId,
    metricType,
    dimensionKey,
    type,
    metricSingleEvent,
    metricNumeratorEvent,
    metricDenominatorEvent,
  ]);
  useEffect(() => {
    if (!Array.isArray(eventParams)) {
      return;
    }
    if (eventParams.length !== 0) {
      query(
        {
          interval: queryTimeRange,
          events: eventParams,
          fields: ['app_platform', dimensionKey],
        },
        {
          onSuccess: (res) => {
            const tableData = res?.res;
            const tables = arrayGroupBy(tableData, 'app_platform');
            setTableData(tables);
            setSummaryData({
              coverage: res?.platform_coverage,
              issues: res?.issues,
            });
          },
          onError: (error: any) => {
            const errorMsg = error?.response?.data?.msg || 'Get validation report error.';
            message.error(errorMsg);
          },
        },
      );
    } else {
      setActiveTool(null);
    }
  }, [query, eventParams, setActiveTool]);

  useEffect(() => {
    if (dimensionListRes?.data) {
      const list = dimensionListRes.data || [];
      const options = list.filter((d) => d.is_for_validation_report === 1);
      setDimensions(options);
    } else {
      setDimensions([]);
    }
  }, [dimensionListRes?.data]);

  return (
    <Spin spinning={isLoading || isDimensionFetching || isEventFetching}>
      <StyledWrapper>
        <Header />
        <StyledContent>
          <SummaryCard summaryData={summaryData} dimension={activeDimension} />
          <CoverageDetail
            tableData={tableData}
            dimensions={dimensions}
            eventTypes={eventTypes}
            onDimensionChange={(activeDimension) => {
              setActiveDimension(activeDimension);
            }}
          />
        </StyledContent>
      </StyledWrapper>
    </Spin>
  );
};

export default Validation;
